import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Swal from "sweetalert2";
import Layout from "../../components/layout/Layout";
import { nowCombosApi } from "../../api/nowCombos";
import { nowMealsApi } from "../../api/nowMeals";
import axios from "axios";

interface Kitchen {
  _id: string;
  name: string;
}

interface NowMeal {
  _id: string;
  name: string;
  image: string;
  currentPrice: number;
}

interface SideItem {
  _id: string;
  name: string;
  price: number;
}

interface ComboItemInput {
  instantMealId: string;
  quantity: number;
  defaultSideId?: string;
}

interface NowCombo {
  _id: string;
  title: string;
  description?: string;
  image?: string;
  kitchenId: { _id: string; name: string } | string;
  items: Array<{
    instantMealId: NowMeal;
    quantity: number;
    defaultSideId?: SideItem;
  }>;
  originalPrice: number;
  comboPrice: number;
  isAvailable: boolean;
  salesCount?: number;
  sortOrder?: number;
}

export default function NowCombos() {
  const [combos, setCombos] = useState<NowCombo[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [availableMeals, setAvailableMeals] = useState<NowMeal[]>([]);
  const [selectedKitchen, setSelectedKitchen] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editingComboId, setEditingComboId] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [dialogKitchenId, setDialogKitchenId] = useState<string>("");
  const [comboPrice, setComboPrice] = useState<number | "">(199);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [comboItems, setComboItems] = useState<ComboItemInput[]>([
    { instantMealId: "", quantity: 1 },
    { instantMealId: "", quantity: 1 },
  ]);

  const API_URL = process.env.REACT_APP_API_URL || "";

  const fetchKitchens = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/kitchens`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      if (res.data && Array.isArray(res.data.kitchens)) {
        setKitchens(res.data.kitchens);
        if (res.data.kitchens.length > 0 && !selectedKitchen) {
          setSelectedKitchen(res.data.kitchens[0]._id);
        }
      }
    } catch (err) {
      console.error("Failed to load kitchens:", err);
    }
  };

  const fetchMeals = async () => {
    try {
      const res = await nowMealsApi.getNowMeals({ limit: 100 });
      if (res && Array.isArray(res.nowMeals)) {
        setAvailableMeals(res.nowMeals);
      }
    } catch (err) {
      console.error("Failed to load meals:", err);
    }
  };

  const fetchCombos = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await nowCombosApi.getAllCombosAdmin(selectedKitchen);
      if (res && res.success) {
        setCombos(res.combos || []);
      }
    } catch (err: any) {
      console.error("Failed to fetch combos:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to load combos", "error");
    } finally {
      setIsLoading(false);
    }
  }, [selectedKitchen]);

  useEffect(() => {
    fetchKitchens();
    fetchMeals();
  }, []);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  // Compute live original price
  const computedOriginalPrice = comboItems.reduce((sum, ci) => {
    const meal = availableMeals.find((m) => m._id === ci.instantMealId);
    return sum + (meal ? meal.currentPrice * (ci.quantity || 1) : 0);
  }, 0);

  const computedSavings = Math.max(0, computedOriginalPrice - (Number(comboPrice) || 0));

  const handleOpenCreateDialog = () => {
    setEditingComboId(null);
    setTitle("");
    setDescription("");
    setImage("");
    setDialogKitchenId(selectedKitchen || (kitchens[0]?._id || ""));
    setComboPrice(199);
    setIsAvailable(true);
    setSortOrder(0);
    setComboItems([
      { instantMealId: "", quantity: 1 },
      { instantMealId: "", quantity: 1 },
    ]);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (combo: NowCombo) => {
    setEditingComboId(combo._id);
    setTitle(combo.title);
    setDescription(combo.description || "");
    setImage(combo.image || "");
    setDialogKitchenId(
      typeof combo.kitchenId === "object" ? combo.kitchenId._id : combo.kitchenId
    );
    setComboPrice(combo.comboPrice);
    setIsAvailable(combo.isAvailable);
    setSortOrder(combo.sortOrder || 0);
    setComboItems(
      combo.items.map((i) => ({
        instantMealId: typeof i.instantMealId === "object" ? i.instantMealId._id : i.instantMealId,
        quantity: i.quantity || 1,
        defaultSideId: typeof i.defaultSideId === "object" ? i.defaultSideId._id : i.defaultSideId,
      }))
    );
    setIsDialogOpen(true);
  };

  const handleAddMealRow = () => {
    setComboItems([...comboItems, { instantMealId: "", quantity: 1 }]);
  };

  const handleRemoveMealRow = (index: number) => {
    if (comboItems.length <= 2) {
      Swal.fire("Warning", "A combo must contain at least 2 instant meals", "warning");
      return;
    }
    const updated = [...comboItems];
    updated.splice(index, 1);
    setComboItems(updated);
  };

  const handleItemChange = (index: number, field: keyof ComboItemInput, value: any) => {
    const updated = [...comboItems];
    updated[index] = { ...updated[index], [field]: value };
    setComboItems(updated);
  };

  const handleSaveCombo = async () => {
    if (!title || !dialogKitchenId || !comboPrice) {
      Swal.fire("Error", "Please fill in title, kitchen, and combo price", "error");
      return;
    }
    if (comboItems.some((ci) => !ci.instantMealId)) {
      Swal.fire("Error", "Please select a valid instant meal for all combo rows", "error");
      return;
    }

    const payload = {
      title,
      description,
      image,
      kitchenId: dialogKitchenId,
      items: comboItems,
      comboPrice: Number(comboPrice),
      isAvailable,
      sortOrder: Number(sortOrder),
    };

    try {
      if (editingComboId) {
        await nowCombosApi.updateNowCombo(editingComboId, payload);
        Swal.fire("Success", "Combo updated successfully", "success");
      } else {
        await nowCombosApi.createNowCombo(payload);
        Swal.fire("Success", "Combo created successfully", "success");
      }
      setIsDialogOpen(false);
      fetchCombos();
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || "Failed to save combo", "error");
    }
  };

  const handleToggleAvailability = async (comboId: string) => {
    try {
      await nowCombosApi.toggleAvailability(comboId);
      fetchCombos();
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || "Failed to toggle availability", "error");
    }
  };

  const handleDeleteCombo = async (comboId: string) => {
    const result = await Swal.fire({
      title: "Delete Combo?",
      text: "Are you sure you want to delete this meal combo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      try {
        await nowCombosApi.deleteNowCombo(comboId);
        Swal.fire("Deleted", "Combo deleted successfully", "success");
        fetchCombos();
      } catch (err: any) {
        Swal.fire("Error", err.response?.data?.message || "Failed to delete combo", "error");
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header Bar */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" fontWeight="700" color="#1C1208">
              Instant Meal Combos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage bundled meal offerings and special discount packages.
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Kitchen</InputLabel>
              <Select
                value={selectedKitchen}
                label="Kitchen"
                onChange={(e) => setSelectedKitchen(e.target.value)}
              >
                {kitchens.map((k) => (
                  <MenuItem key={k._id} value={k._id}>
                    {k.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCombos}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#E67E22", "&:hover": { backgroundColor: "#D35400" } }}
              onClick={handleOpenCreateDialog}
            >
              New Combo
            </Button>
          </Stack>
        </Stack>

        {/* Combos Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : combos.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <LocalOfferIcon sx={{ fontSize: 48, color: "#CCCCCC", mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No combos created yet for this kitchen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create bundled meal packages to boost customer order value.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#E67E22" }}
              onClick={handleOpenCreateDialog}
            >
              Create First Combo
            </Button>
          </Card>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#FFF8F2" }}>
                <TableRow>
                  <TableCell>Combo Title</TableCell>
                  <TableCell>Included Meals</TableCell>
                  <TableCell align="center">Original Price</TableCell>
                  <TableCell align="center">Combo Price</TableCell>
                  <TableCell align="center">Savings</TableCell>
                  <TableCell align="center">Sales</TableCell>
                  <TableCell align="center">Available</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combos.map((combo) => {
                  const savings = Math.max(0, combo.originalPrice - combo.comboPrice);
                  return (
                    <TableRow key={combo._id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={combo.image}
                            alt={combo.title}
                            variant="rounded"
                            sx={{ width: 48, height: 48 }}
                          />
                          <Box>
                            <Typography fontWeight="700" color="#1C1208">
                              {combo.title}
                            </Typography>
                            {combo.description && (
                              <Typography variant="caption" color="text.secondary">
                                {combo.description}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {combo.items.map((it, idx) => (
                            <Typography key={idx} variant="body2" color="text.primary">
                              • {it.quantity}x {it.instantMealId?.name || "Meal"}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{ textDecoration: "line-through", color: "text.secondary" }}
                        >
                          ₹{combo.originalPrice}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle1" fontWeight="700" color="#E67E22">
                          ₹{combo.comboPrice}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`Save ₹${savings}`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: "700" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<TrendingUpIcon sx={{ fontSize: "16px !important", color: "#E67E22 !important" }} />}
                          label={`${combo.salesCount || 0} Sold`}
                          size="small"
                          sx={{ backgroundColor: "#FFF3E0", color: "#E67E22", fontWeight: "600" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={combo.isAvailable}
                          onChange={() => handleToggleAvailability(combo._id)}
                          color="warning"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(combo)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteCombo(combo._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create / Edit Combo Dialog */}
        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle fontWeight="700">
            {editingComboId ? "Edit Instant Combo" : "Create New Instant Combo"}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Combo Title"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Energy Boost Combo"
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief summary of what's inside this combo bundle"
              />

              <TextField
                label="Image URL"
                fullWidth
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="https://..."
              />

              <FormControl fullWidth required>
                <InputLabel>Kitchen</InputLabel>
                <Select
                  value={dialogKitchenId}
                  label="Kitchen"
                  onChange={(e) => setDialogKitchenId(e.target.value)}
                >
                  {kitchens.map((k) => (
                    <MenuItem key={k._id} value={k._id}>
                      {k.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Constituent Meals Picker */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="700">
                    Constituent Meals (Min 2 Items)
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddMealRow}>
                    Add Meal Row
                  </Button>
                </Stack>

                <Stack spacing={1.5}>
                  {comboItems.map((itemRow, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                        <FormControl size="small" sx={{ flex: 2 }}>
                          <InputLabel>Select Meal</InputLabel>
                          <Select
                            value={itemRow.instantMealId}
                            label="Select Meal"
                            onChange={(e) => handleItemChange(idx, "instantMealId", e.target.value)}
                          >
                            {availableMeals.map((m) => (
                              <MenuItem key={m._id} value={m._id}>
                                {m.name} (₹{m.currentPrice})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          label="Qty"
                          type="number"
                          size="small"
                          sx={{ width: 80 }}
                          value={itemRow.quantity}
                          onChange={(e) =>
                            handleItemChange(idx, "quantity", parseInt(e.target.value) || 1)
                          }
                        />

                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveMealRow(idx)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              {/* Live Savings & Pricing Callout */}
              <Paper sx={{ p: 2, backgroundColor: "#FFF8F2", borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Sum of Original Meals Price:
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      ₹{computedOriginalPrice}
                    </Typography>
                  </Box>

                  <TextField
                    label="Combo Discounted Price (₹)"
                    type="number"
                    size="small"
                    required
                    value={comboPrice}
                    onChange={(e) =>
                      setComboPrice(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={{ width: 180 }}
                  />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Customer Savings:
                    </Typography>
                    <Chip
                      label={`Save ₹${computedSavings}`}
                      color={computedSavings > 0 ? "success" : "default"}
                      sx={{ fontWeight: "700", mt: 0.5 }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveCombo}
              sx={{ backgroundColor: "#E67E22", "&:hover": { backgroundColor: "#D35400" } }}
            >
              {editingComboId ? "Update Combo" : "Create Combo"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
